export class SearchForm extends HTMLFormElement {
    private dropdowns: Map<HTMLElement, DropdownController> = new Map();

    constructor() {
        super();
    }

    connectedCallback() {
        this.initializeDropdowns();
        this.updateOffsets();
        window.addEventListener('resize', () => this.updateOffsets());

        // CFB-443 : autocomplete « Où souhaitez-vous vous former » (ville + code postal).
        const locationInput = this.querySelector<HTMLInputElement>('#input-location');
        if (locationInput) {
            new LocationAutocomplete(locationInput);
        }
    }

    private initializeDropdowns() {
        const dropdownTriggers = this.querySelectorAll('[data-bs-toggle="dropdown"]');
        
        dropdownTriggers.forEach((trigger) => {
            const triggerId = (trigger as HTMLElement).id;
            if (!triggerId) return;
            
            const dropdownMenu = this.querySelector(`[aria-labelledby="${triggerId}"]`);
            if (!dropdownMenu) return;
            
            const closeBtn = dropdownMenu.querySelector('[data-close-dropdown]');
            
            const controller = new DropdownController(
                trigger as HTMLElement,
                dropdownMenu as HTMLElement,
                closeBtn as HTMLElement | null
            );
            this.dropdowns.set(trigger as HTMLElement, controller);
        });
    }

    private updateOffsets() {
        const isMobile = window.innerWidth < 992;
        const offset = isMobile ? '-29,22' : '-52,22';
        
        const dropdownTriggers = this.querySelectorAll('[data-bs-toggle="dropdown"]');
        dropdownTriggers.forEach((trigger) => {
            (trigger as HTMLElement).setAttribute('data-bs-offset', offset);
        });
    }
}

/**
 * Autocomplete de localisation (ville + code postal) pour le champ « Où souhaitez-vous
 * vous former » de l'accueil (CFB-443). Interroge /api/recherche/localisations et propose
 * les villes du catalogue ; à la sélection, le champ est rempli avec le nom de ville
 * (le filtre serveur matche « ville contient » + code postal).
 */
interface LocationSuggestion {
    ville: string;
    cp: string | null;
}

class LocationAutocomplete {
    private static readonly ENDPOINT = '/api/recherche/localisations';
    private static readonly DEBOUNCE_MS = 250;
    private static readonly MIN_CHARS = 2;

    private input: HTMLInputElement;
    private menu: HTMLElement;
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private abortController: AbortController | null = null;

    constructor(input: HTMLInputElement) {
        this.input = input;
        this.input.setAttribute('autocomplete', 'off');

        // Conteneur des suggestions, positionné sous le champ.
        const parent = this.input.parentElement!;
        parent.classList.add('position-relative');

        this.menu = document.createElement('div');
        this.menu.className = 'dropdown-menu location-autocomplete w-100 py-0';
        this.menu.setAttribute('role', 'listbox');
        this.menu.style.top = '100%';
        this.menu.style.left = '0';
        this.menu.style.maxHeight = '260px';
        this.menu.style.overflowY = 'auto';
        parent.appendChild(this.menu);

        this.setup();
    }

    private setup() {
        this.input.addEventListener('input', () => this.onInput());
        this.input.addEventListener('focus', () => {
            if (this.menu.childElementCount > 0) this.show();
        });
        document.addEventListener('click', (e) => {
            const target = e.target as Node;
            if (!this.input.contains(target) && !this.menu.contains(target)) this.hide();
        });
        this.input.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') this.hide();
        });
    }

    private onInput() {
        const q = this.input.value.trim();
        if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
        if (q.length < LocationAutocomplete.MIN_CHARS) {
            this.hide();
            return;
        }
        this.debounceTimer = setTimeout(() => this.fetchSuggestions(q), LocationAutocomplete.DEBOUNCE_MS);
    }

    private async fetchSuggestions(q: string) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        try {
            const res = await fetch(
                `${LocationAutocomplete.ENDPOINT}?q=${encodeURIComponent(q)}`,
                { signal: this.abortController.signal, headers: { Accept: 'application/json' } }
            );
            if (!res.ok) return;
            const data = (await res.json()) as LocationSuggestion[];
            this.render(data);
        } catch {
            // requête annulée ou erreur réseau : on ignore silencieusement.
        }
    }

    private render(items: LocationSuggestion[]) {
        this.menu.innerHTML = '';
        if (!items || items.length === 0) {
            this.hide();
            return;
        }
        for (const item of items) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'dropdown-item text-wrap';
            btn.textContent = item.cp ? `${item.ville} (${item.cp})` : item.ville;
            btn.addEventListener('click', () => {
                // On filtre sur la ville ; le champ affiche le nom retenu.
                this.input.value = item.ville;
                this.hide();
                this.input.focus();
            });
            this.menu.appendChild(btn);
        }
        this.show();
    }

    private show() { this.menu.classList.add('show'); }
    private hide() { this.menu.classList.remove('show'); }
}

class DropdownController {
    private trigger: HTMLElement;
    private menu: HTMLElement;
    private closeBtn: HTMLElement | null;
    private dropdown: any = null;

    constructor(trigger: HTMLElement, menu: HTMLElement, closeBtn: HTMLElement | null) {
        this.trigger = trigger;
        this.menu = menu;
        this.closeBtn = closeBtn;
        
        this.setup();
    }

    private setup() {
        this.dropdown = (window as any).bootstrap?.Dropdown 
            ? new (window as any).bootstrap.Dropdown(this.trigger, {
                autoClose: false
            })
            : null;

        this.closeBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.close();
        });

        this.menu.addEventListener('click', (e) => {
            e.stopPropagation();
        });

        document.addEventListener('click', (e) => {
            this.handleDocumentClick(e);
        });
    }

    private handleDocumentClick(e: Event) {
        const target = e.target as HTMLElement;
        
        if (this.closeBtn?.contains(target)) {
            this.close();
            return;
        }
        
        if (target === this.trigger || this.trigger.contains(target)) { 
            return;
        }

        if (this.menu.contains(target)) {
            return;
        }

        this.close();
    }

    private close() {
        this.menu.classList.remove('show');
        this.trigger.setAttribute('aria-expanded', 'false');

        if (this.dropdown) {
            this.dropdown.hide();
        }
    }
}
